/**
 * Reading a file without freezing the tab.
 *
 * One worker per file, ended as soon as it answers: a file is read once in a while,
 * so a worker kept alive between them holds a parsed workbook's memory for nothing,
 * and a fresh one cannot be left in a bad state by the last file.
 *
 * Nothing here is awaited before a vendor's picker opens — the screens only call this
 * with bytes already in hand — so the one-click-one-window rule is untouched.
 */
import { IngestError, type IngestResult } from "@broadpaper/ingest";
import type { ReadReply, ReadRequest } from "./read.js";

export type { ReadRequest } from "./read.js";

export function readOffThread(request: ReadRequest): Promise<IngestResult> {
  if (typeof Worker === "undefined") return inline(request);
  let worker: Worker;
  try {
    worker = new Worker(new URL("./ingest.worker.ts", import.meta.url), { type: "module" });
  } catch {
    return inline(request);
  }

  return new Promise<IngestResult>((resolve, reject) => {
    let answered = false;
    worker.onmessage = (event: MessageEvent<ReadReply>) => {
      answered = true;
      worker.terminate();
      try {
        resolve(unwrap(event.data));
      } catch (e) {
        reject(e);
      }
    };
    worker.onmessageerror = () => {
      answered = true;
      worker.terminate();
      reject(new Error("The file was read, but its contents could not be handed back to the page."));
    };
    worker.onerror = (event) => {
      event.preventDefault();
      worker.terminate();
      if (answered) return;
      // The worker never started — a policy that forbids workers, or a script that
      // failed to load. Reading on the page is slower and still correct, which beats
      // telling somebody their perfectly good file could not be read.
      inline(request).then(resolve, reject);
    };
    worker.postMessage(request);
  });
}

/** A reply as the screens expect it: the result, or the error they already know how to explain. */
export function unwrap(reply: ReadReply): IngestResult {
  if (reply.ok) return reply.result;
  if (reply.ingest) throw new IngestError(reply.message, reply.hint);
  throw new Error(reply.message);
}

async function inline(request: ReadRequest): Promise<IngestResult> {
  const { answer } = await import("./read.js");
  return unwrap(await answer(request));
}
